import React, { useState, useCallback, useEffect, Fragment } from 'react';
import Router from 'next/router';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Typo, Buttons } from 'tmt-ui-kit';
import { isEmpty } from 'lodash';
// import { scroller } from 'react-scroll';

import { actions as sessionActions } from '~/redux/reducers/session';
import { upload } from '~/utils/media';
import scrollToFn from '~/utils/scroller';

import useSignUpForm, { schema } from './hooks/useSignUpForm';
import Submitting from '~/components/Affiliate/Submitting';
import StepOneForm from './StepOneForm';
import StepTwoForm from './StepTwoForm';
import UserForm from './UserForm';

const Container = styled.div`
  box-sizing: border-box;
  box-shadow: 0 9px 12px 0 rgba(0, 0, 0, 0.1);
  transform: translateY(-80px);
  background: white;
  border-radius: 4px;

  @media(max-width: 768px) {
    transform: translateY(0px);
    margin-top: 50px;
  }
`;

const Header = styled.div`
  padding: 34px 49px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  @media(max-width: 600px) {
    padding: 24px 16px;
  }
`;

const Title = styled(Typo.HeaderTwo)`
  color: ${props => props.theme.orange} !important;
  font-size: 24px !important;
  font-weight: 600 !important;
`;

const Text = styled(Typo.Paragraph)`
  font-size: 16px !important;
`;

const FormContainer = styled.form`
  background: ${props => props.theme.blueGrayLighter};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 24px 0px 0px;
`;

const SubLabel = styled(Typo.Paragraph)`
  text-align: left !important;
`;

const Submit = styled(Buttons.Button.OrangeButton)`
  width: 148px;
  display: block;
  margin: 0px;

  ${props => props.disabled && `
    filter: grayscale(100%);
    cursor: not-allowed;
    * {
      cursor: not-allowed;
      pointer-events:none;
    }
  `}

  @media(max-width: 600px) {
    padding: 8px 3px;
    height: 42px;
    width: 112px;
  }
`;

const TextError = styled(Typo.HeaderTwo)`
  color: ${props => props.theme.red} !important;
  text-align: center;
  margin-top: 40px;
`;

const ActionStep = styled.div`
  width: 100%;
  display: flex;
  margin: 32px auto;
  padding: 24px 50px 62px;
  justify-content: ${props => (props.step === '1' ? 'flex-end' : 'space-between')};

  @media(max-width: 600px) {
    padding: 24px 16px 62px;
    margin: 0px;
  }
`;

const Previous = styled(Buttons.Button.TextButton)`
  width: 148px;
  display: block;
  background: transparent;
  color: ${props => props.theme.orange};
  font-weight: 500;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
  border: solid 1px ${props => props.theme.orange};

  @media(max-width: 600px) {
    padding: 8px 3px;
    height: 42px;
    width: 112px;
    margin: 0;
  }
`;

const Next = styled(Submit)``;

const Flex = styled.div`
  display: flex;
`;

const TextStep = styled(SubLabel)`
  margin-right: 12px;

  @media(max-width: 600px) {
    margin-right: 4px;
  }
`;


const enhance = connect(
  state => ({
    user: state.session.user,
    signingUp: state.session.signingUp,
    logout: state.session.logout,
    signupErrorMessage: state.session.signupErrorMessage,
    isLoggedIn: state.session.isLoggedIn,
    isFluencer: state.session.isFluencer,
  }),
  {
    ...sessionActions,
  }
);

function handleDisabled(step, user, values) {
  let isDisabled = false;
  const isUser = !values.email
                 || !values.firstName
                 || !values.lastName
                 || !values.password;
  if (step === '1') {
    isDisabled = !values.aboutYourself
                 || !values.tripName
                 || !values.quantity
                 || !values.planTrip;
  } else {
    isDisabled = !values.websiteUrl
                 || !values.targetCountry
                 || !values.isGdpr
                 || !values.isAgree;
  }
  if (isEmpty(user)) {
    return isDisabled || isUser;
  }
  return isDisabled;
}

function handleError(errors, user) {
  const isUser = !errors.email
                 || !errors.firstName
                 || !errors.lastName
                 || !errors.password;
  const stepOne = errors.aboutYourself
                  || !errors.tripName
                  || errors.quantity
                  || errors.planTrip;
  const stepTwo = errors.websiteUrl
                  || errors.targetCountry
                  || errors.isGdpr
                  || errors.isAgree
                  || errors.providence;
  if (isEmpty(user)) {
    return { stepOne: (stepOne || isUser), stepTwo };
  }
  return { stepOne, stepTwo };
}

function scrollTo(name, offset) {
  scrollToFn(name, {
    offset,
    duration: 500,
    smooth: true,
  });
}

const Register = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('1');

  const onSubmit = useCallback(async (data) => {
    const isValid = await schema.isValid(data);
    if (isValid) {
      const influencerDetail = {
        status: 'pending',
        about_yourself: data.aboutYourself,
        trip_name: data.tripName,
        quantity: data.quantity,
        plan_trip: data.planTrip,
        website_url: data.websiteUrl,
        providence: data.providence,
        target_audience_country: data.targetCountry,
        is_gdpr_consent: data.isGdpr,
      };
      if (data.affiliateId) influencerDetail.affiliate_id = data.affiliateId;
      if (data.needToBeAffiliate) influencerDetail.need_to_be_affiliated = data.needToBeAffiliate;
      if (data.feacbookUrl) influencerDetail.facebook_url = data.feacbookUrl;
      if (data.instagramUrl) influencerDetail.instagram_url = data.instagramUrl;
      if (data.twitterUrl) influencerDetail.twitter_url = data.twitterUrl;
      if (data.youtubeUrl) influencerDetail.youtube_url = data.youtubeUrl;
      if (data.otherMessage) influencerDetail.other_message = data.otherMessage;

      if (isEmpty(props.user)) {
        const signUpData = {
          email: data.email,
          password: data.password,
          name: {
            first: data.firstName,
            last: data.lastName,
          },
        };
        await props.signup(signUpData);
        if (data.fileUpload) {
          influencerDetail.analytics_screenshot = await upload(data.fileUpload, 'users');
        }
        await props.setAffiliateInfluencer(influencerDetail);
      } else {
        if (data.fileUpload) {
          influencerDetail.analytics_screenshot = await upload(data.fileUpload, 'users');
        }
        await props.setAffiliateInfluencer(influencerDetail);
      }

      setIsSubmitting(true);
      const scrollName = props.isMobile ? 'register-mebile' : 'register-desktop';
      const offset = props.isMobile ? -100 : -200;
      return scrollTo(scrollName, offset);
    }
    return false;
  }, [props]);

  const onClickAction = useCallback((type) => {
    setStep(type);
    const scrollName = props.isMobile ? 'register-mebile' : 'register-desktop';
    const offset = props.isMobile ? 150 : 110;
    scrollTo(scrollName, offset);
  }, [props.isMobile]);

  const signUpForm = useSignUpForm(undefined, onSubmit);
  const { user, isFluencer, signingUp } = props;

  useEffect(() => {
    if (!isEmpty(user)) signUpForm.change('isUser', true);
  }, [user]);

  useEffect(() => {
    if (!isEmpty(signUpForm.errors)) {
      const { stepOne, stepTwo } = handleError(signUpForm.errors, user);
      if (stepOne) setStep('1');
      if (stepTwo) setStep('2');
      const scrollName = props.isMobile ? 'register-mebile' : 'register-desktop';
      const offset = props.isMobile ? 150 : 110;
      scrollTo(scrollName, offset);
    }
  }, [signUpForm.errors]);

  const isDisabledNext = handleDisabled(step, user, signUpForm.values);
  const isDisabledSubmit = handleDisabled(step, user, signUpForm.values);
  const logout = () => props.logout().then(() => Router.push('/influencer'));
  return (
    <Container>
      <Header>
        {!isSubmitting ? (
          <Fragment>
            <Title>We would like to know you better!</Title>
            <Text>Please kindly fill in your information below. <br /><br />Should you have any other requests or questions, please include them in &quot;Other Message&quot;. <br /><br />We look forward to hearing from you!</Text>
          </Fragment>
        ) : (
          <Fragment>
            <Submitting />
          </Fragment>
        )}
      </Header>
      {!isSubmitting && (
        <FormContainer {...signUpForm.getFormProps()}>
          {step === '1' && <UserForm signUpForm={signUpForm} user={props.user} logout={logout} />}
          {step === '1' && <StepOneForm signUpForm={signUpForm} />}
          {step === '2' && <StepTwoForm signUpForm={signUpForm} />}
          <ActionStep step={step}>
            {step === '1' && (
              <Fragment>
                <TextStep>{step}/2 step</TextStep>
                <Next onClick={() => onClickAction('2')} disabled={isDisabledNext}>Next</Next>
              </Fragment>
            )}
            {step === '2' && <Previous onClick={() => onClickAction('1')}>Previous</Previous>}
            {step === '2' && (
              <Flex>
                <TextStep>{step}/2 step</TextStep>
                <Submit
                  // onClick={() => onSubmit(signUpForm.values)}
                  disabled={isDisabledSubmit || isFluencer || signingUp}
                >
                  Submit
                </Submit>
              </Flex>
            )}
          </ActionStep>
          <TextError>{props.signupErrorMessage}</TextError>
        </FormContainer>
      )}
    </Container>
  );
};

export default enhance(Register);
